/**
 * Admin Service
 * Handles all admin-related API calls
 */
import axios from 'axios';
import { getToken } from './authService';

const API_URL = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001') + '/api/admin';

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${getToken()}` }
});

/**
 * Get all users with pagination
 */
export const getUsers = async (limit = 100, offset = 0) => {
  const response = await axios.get(`${API_URL}/users?limit=${limit}&offset=${offset}`, getAuthHeaders());
  return response.data;
};

/**
 * Get platform statistics
 */
export const getStats = async () => {
  const response = await axios.get(`${API_URL}/stats`, getAuthHeaders());
  return response.data;
};

/**
 * Delete a user
 */
export const deleteUser = async (userId) => {
  const response = await axios.delete(`${API_URL}/users/${userId}`, getAuthHeaders());
  return response.data;
};

/**
 * Update user calculation limit
 */
export const setUserLimit = async (userId, newLimit) => {
  const response = await axios.put(
    `${API_URL}/users/${userId}/set-limit`,
    { calculation_limit: newLimit },
    getAuthHeaders()
  );
  return response.data;
};

/**
 * Reset user's calculation count
 */
export const resetUserUsage = async (userId) => {
  const response = await axios.post(`${API_URL}/users/${userId}/reset-usage`, {}, getAuthHeaders());
  return response.data;
};

/**
 * Toggle admin status for a user
 */
export const toggleAdmin = async (userId, makeAdmin) => {
  const response = await axios.post(
    `${API_URL}/users/${userId}/make-admin`,
    { is_admin: makeAdmin },
    getAuthHeaders()
  );
  return response.data;
};

/**
 * Get all emission factors, optionally filtered by transport mode
 */
export const getEmissionFactors = async (transportMode = null) => {
  const params = transportMode ? `?transport_mode=${transportMode}` : '';
  const response = await axios.get(`${API_URL}/emission-factors${params}`, getAuthHeaders());
  return response.data;
};

/**
 * Update an emission factor
 */
export const updateEmissionFactor = async (factorId, updates) => {
  const response = await axios.put(
    `${API_URL}/emission-factors/${factorId}`,
    updates,
    getAuthHeaders()
  );
  return response.data;
};

/**
 * Create a new emission factor
 */
export const createEmissionFactor = async (data) => {
  const response = await axios.post(`${API_URL}/emission-factors`, data, getAuthHeaders());
  return response.data;
};

/**
 * Delete an emission factor
 */
export const deleteEmissionFactor = async (factorId) => {
  const response = await axios.delete(`${API_URL}/emission-factors/${factorId}`, getAuthHeaders());
  return response.data;
};

/**
 * Get Scope 3 emission factors (Cat 6 + Cat 7), optionally filtered.
 */
export const getScope3Factors = async ({ ghgCategory = null, mode = null } = {}) => {
  const params = new URLSearchParams();
  if (ghgCategory !== null) params.set('ghg_category', ghgCategory);
  if (mode) params.set('mode', mode);
  const qs = params.toString();
  const response = await axios.get(
    `${API_URL}/scope3-factors${qs ? `?${qs}` : ''}`,
    getAuthHeaders()
  );
  return response.data;
};

/**
 * Edit a Scope 3 factor's numeric values + display name. Edit-only —
 * hierarchy fields (mode, vehicle_type, fuel_type, etc.) are immutable.
 */
export const updateScope3Factor = async (factorId, updates) => {
  const response = await axios.patch(
    `${API_URL}/scope3-factors/${factorId}`,
    updates,
    getAuthHeaders()
  );
  return response.data;
};

/**
 * Reset Scope 3 Cat 6/7 factors to the DEFRA baseline from
 * backend/scope3_factors_seed.py. Overwrites any admin edits.
 */
export const resetScope3FactorsToDefra = async () => {
  const response = await axios.post(
    `${API_URL}/scope3-factors/reimport`,
    {},
    getAuthHeaders()
  );
  return response.data;
};

export default {
  getUsers,
  getStats,
  deleteUser,
  setUserLimit,
  resetUserUsage,
  toggleAdmin,
  getEmissionFactors,
  updateEmissionFactor,
  createEmissionFactor,
  deleteEmissionFactor,
  getScope3Factors,
  updateScope3Factor,
  resetScope3FactorsToDefra,
};
